import React, { useState, createContext, useContext } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://nomokratia.ai';

// ============================================================
// AUTH CONTEXT - Use across all components
// ============================================================
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('nomokratia_user');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem('nomokratia_user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('nomokratia_user');
    localStorage.removeItem('nomokratia_token');
  };

  const isLoggedIn = !!user;

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoggedIn }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================
// AUTH GATE - Wraps any submit action
// ============================================================
export const useAuthGate = () => {
  const [showModal, setShowModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const auth = useAuth();

  const guardedAction = (action) => {
    const _tok = localStorage.getItem('token') || localStorage.getItem('nomokratia_token');
    if (_tok || (auth && auth.isLoggedIn)) {
      action();
    } else {
      setPendingAction(() => action);
      setShowModal(true);
    }
  };

  const onSuccess = () => {
    setShowModal(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  return { showModal, setShowModal, guardedAction, onSuccess };
};

// ============================================================
// PLANS DATA
// ============================================================
const PLANS = [
  {
    id: 'free_trial',
    name: 'Δοκιμαστικό',
    price: '0',
    period: '14 ημέρες πλήρης πρόσβαση',
    icon: '',
    color: '#1E293B',
    bg: '#FFFFFF',
    border: '#E2E8F0',
    features: ['Πλήρες CRM', 'AI Νομική Αναζήτηση', '10 AI ερωτήσεις/ημέρα', 'Χωρίς κάρτα'],
    popular: false,
  },
  {
    id: 'starter',
    name: 'Επαγγελματικό',
    price: '199',
    period: '/μήνα',
    icon: '',
    color: '#1E293B',
    bg: '#FFFFFF',
    border: '#1E293B',
    features: ['Όλα τα Δοκιμαστικού', 'Απεριόριστες AI ερωτήσεις', 'AADE Σύνδεση', 'Τιμολόγηση & Χρονοχρέωση', 'Conflict Check', 'Email Υποστήριξη'],
    popular: true,
  },
  {
    id: 'professional',
    name: 'Δικηγορικό Γραφείο',
    price: 'Κατόπιν',
    period: 'επικοινωνίας',
    icon: '',
    color: '#64748B',
    bg: '#FFFFFF',
    border: '#E2E8F0',
    features: ['Από 2+ δικηγόρους', 'Όλα τα Επαγγελματικού', 'Custom Integrations', 'Priority Support'],
    popular: false,
  },
];

const BAR_ASSOCIATIONS = [
  'Δ.Σ. Αθηνών', 'Δ.Σ. Θεσσαλονίκης', 'Δ.Σ. Πειραιώς', 'Δ.Σ. Πατρών',
  'Δ.Σ. Ηρακλείου', 'Δ.Σ. Λάρισας', 'Δ.Σ. Βόλου', 'Δ.Σ. Ιωαννίνων',
  'Δ.Σ. Καβάλας', 'Δ.Σ. Σερρών', 'Δ.Σ. Ρόδου', 'Δ.Σ. Κομοτηνής',
  'Δ.Σ. Χανίων', 'Δ.Σ. Κέρκυρας', 'Δ.Σ. Λαμίας', 'Δ.Σ. Τρικάλων',
  'Δ.Σ. Καλαμάτας', 'Δ.Σ. Χαλκιδικής', 'Δ.Σ. Κοζάνης', 'Δ.Σ. Δράμας',
  'Άλλος',
];

const LEGAL_AREAS = [
  'Αστικό Δίκαιο', 'Ποινικό Δίκαιο', 'Εμπορικό Δίκαιο', 'Διοικητικό Δίκαιο',
  'Εργατικό Δίκαιο', 'Οικογενειακό Δίκαιο', 'Φορολογικό Δίκαιο', 'Κτηματολογικό',
  'Πτωχευτικό Δίκαιο', 'Τραπεζικό Δίκαιο', 'Ναυτικό Δίκαιο', 'Ευρωπαϊκό Δίκαιο',
  'Δίκαιο Πληροφορικής', 'Περιβαλλοντικό Δίκαιο', 'Ιατρικό Δίκαιο',
];

const EMPLOYEE_OPTIONS = ['Μόνο εγώ', '2-3', '4-10', '11-30', '30+'];

// ============================================================
// MODAL COMPONENT
// ============================================================
export const AuthModal = ({ show, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1=plans, 2=register, 3=login
  const [selectedPlan, setSelectedPlan] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const auth = useAuth();

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', firm_name: '',
    phone: '', mobile: '',
    vat_number: '', doy: '',
    bar_number: '', bar_association: '',
    office_address: '', office_city: '', office_zip: '',
    legal_areas: [],
    employee_count: '',
  });

  if (!show) return null;

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setStep(2);
  };

  const handleRegister = async () => {
    if (!form.first_name || !form.last_name || !form.email || !form.password) {
      setError('Συμπληρώστε τα υποχρεωτικά πεδία');
      return;
    }
    if (!form.vat_number || form.vat_number.length !== 9) {
      setError('Εισάγετε έγκυρο ΑΦΜ (9 ψηφία)');
      return;
    }
    if (!form.mobile) {
      setError('Το κινητό τηλέφωνο είναι υποχρεωτικό');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(API + '/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          plan: selectedPlan,
          legal_areas: form.legal_areas,
          firm_name: form.firm_name || '',
        }),
      });

      const data = await res.json();

      if (res.ok && data.success !== false) {
        const userData = data.user || {
          name: form.first_name + ' ' + form.last_name,
          email: form.email,
          plan: selectedPlan,
          trial_end: selectedPlan === 'free_trial' ? new Date(Date.now() + 30*24*60*60*1000).toISOString() : null,
        };

        if (data.token) {
          localStorage.setItem('nomokratia_token', data.token);
        }

        auth.login(userData);

        if (selectedPlan !== 'free_trial' && data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          onSuccess();
        }
      } else {
        setError(data.message || data.error || 'Σφάλμα εγγραφής');
      }
    } catch (e) {
      // Fallback: save locally if backend not ready
      const userData = {
        name: form.first_name + ' ' + form.last_name,
        email: form.email,
        plan: selectedPlan,
        trial_end: selectedPlan === 'free_trial' ? new Date(Date.now() + 30*24*60*60*1000).toISOString() : null,
        registered_at: new Date().toISOString(),
      };
      auth.login(userData);
      onSuccess();
    }

    setLoading(false);
  };

  const handleLogin = async () => {
    if (!form.email || !form.password) {
      setError('Συμπληρώστε email και κωδικό');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const body = { email: form.email, password: form.password };
      if (form.totp_code) body.totp_code = form.totp_code;
      if (form.backup_code) body.backup_code = form.backup_code;

      const res = await fetch(API + '/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.requires_2fa) {
        setForm({...form, requires2fa: true});
        setLoading(false);
        return;
      }

      if (res.ok && data.token) {
        localStorage.setItem('nomokratia_token', data.token);
        auth.login(data.user);
        onSuccess();
      } else {
        setError(data.detail || data.message || 'Λάθος στοιχεία');
      }
    } catch (e) {
      setError('Σφάλμα σύνδεσης');
    }
    setLoading(false);
  };

  const toggleLegalArea = (area) => {
    setForm(prev => ({
      ...prev,
      legal_areas: prev.legal_areas.includes(area)
        ? prev.legal_areas.filter(a => a !== area)
        : [...prev.legal_areas, area]
    }));
  };

  const overlay = {
    position:'fixed',top:0,left:0,right:0,bottom:0,
    background:'rgba(0,0,0,0.5)',backdropFilter:'blur(4px)',
    display:'flex',alignItems:'center',justifyContent:'center',
    zIndex:9999,padding:20,
  };
  const modal = {
    background:'#FFF',borderRadius:20,maxWidth:step===1?800:560,width:'100%',
    maxHeight:'90vh',overflowY:'auto',position:'relative',
    boxShadow:'0 20px 60px rgba(0,0,0,0.15)',
  };
  const inp = {width:'100%',padding:'10px 14px',borderRadius:10,border:'1px solid #E2E8F0',fontSize:14,outline:'none',boxSizing:'border-box'};
  const lbl = {display:'block',fontSize:12,fontWeight:600,color:'#374151',marginBottom:4};

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e=>e.stopPropagation()}>

        {/* Close button */}
        <button onClick={onClose} style={{
          position:'absolute',top:16,right:16,width:32,height:32,borderRadius:8,
          border:'none',background:'#F1F5F9',cursor:'pointer',fontSize:18,color:'#64748B',
          display:'flex',alignItems:'center',justifyContent:'center',
        }}>&#10005;</button>

        {/* ============ STEP 1: PLAN SELECTION ============ */}
        {step === 1 && (
          <div style={{padding:32}}>
            <div style={{textAlign:'center',marginBottom:24}}>
              <h2 style={{fontSize:24,fontWeight:800,color:'#1E293B',margin:'0 0 8px'}}>
                Ξεκινήστε με το Nomokratia.ai
              </h2>
              <p style={{fontSize:14,color:'#64748B',margin:0}}>
                Επιλέξτε το πλάνο που σας ταιριάζει
              </p>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16}}>
              {PLANS.map(plan => (
                <div key={plan.id} onClick={()=>handlePlanSelect(plan.id)} style={{
                  padding:24,borderRadius:16,cursor:'pointer',textAlign:'center',
                  border:`2px solid ${plan.popular?plan.color:plan.border}`,
                  background:plan.bg,position:'relative',transition:'transform 0.2s',
                }}>
                  {plan.popular && (
                    <div style={{
                      position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',
                      background:plan.color,color:'#FFF',fontSize:11,fontWeight:700,
                      padding:'4px 12px',borderRadius:20,
                    }}>ΔΗΜΟΦΙΛΕΣ</div>
                  )}
                  <div style={{fontSize:36,marginBottom:8}}>{plan.icon}</div>
                  <div style={{fontSize:16,fontWeight:700,color:'#1E293B'}}>{plan.name}</div>
                  <div style={{fontSize:28,fontWeight:800,color:plan.color,margin:'8px 0'}}>
                    {plan.price === '0' ? 'Δωρεάν' : `€${plan.price}`}
                  </div>
                  <div style={{fontSize:12,color:'#64748B',marginBottom:12}}>{plan.period}</div>
                  <div style={{textAlign:'left'}}>
                    {plan.features.map((f,i) => (
                      <div key={i} style={{fontSize:12,color:'#475569',padding:'3px 0',display:'flex',alignItems:'center',gap:6}}>
                        <span style={{color:plan.color}}>&#10003;</span> {f}
                      </div>
                    ))}
                  </div>
                  <button style={{
                    marginTop:16,width:'100%',padding:'10px',borderRadius:10,
                    background:plan.popular?plan.color:'#FFF',
                    color:plan.popular?'#FFF':plan.color,
                    border:`2px solid ${plan.color}`,fontWeight:700,fontSize:13,cursor:'pointer',
                  }}>
                    {plan.price === '0' ? 'Δοκιμάστε Δωρεάν' : 'Επιλογή'}
                  </button>
                </div>
              ))}
            </div>

            <div style={{textAlign:'center',marginTop:20}}>
              <button onClick={()=>setStep(3)} style={{
                background:'none',border:'none',color:'#3B82F6',fontSize:14,
                cursor:'pointer',textDecoration:'underline',
              }}>
                Έχετε ήδη λογαριασμό; Συνδεθείτε
              </button>
            </div>
          </div>
        )}

        {/* ============ STEP 2: REGISTRATION ============ */}
        {step === 2 && (
          <div style={{padding:32}}>
            <button onClick={()=>setStep(1)} style={{
              padding:'4px 10px',borderRadius:6,border:'1px solid #E2E8F0',
              background:'#FFF',cursor:'pointer',fontSize:12,marginBottom:16,
            }}>&#8592; Πίσω</button>

            <div style={{textAlign:'center',marginBottom:20}}>
              <span style={{fontSize:32}}>
                {PLANS.find(p=>p.id===selectedPlan)?.icon}
              </span>
              <h2 style={{fontSize:20,fontWeight:800,color:'#1E293B',margin:'8px 0 4px'}}>
                Εγγραφή - {PLANS.find(p=>p.id===selectedPlan)?.name}
              </h2>
              <p style={{fontSize:13,color:'#64748B',margin:0}}>Συμπληρώστε τα στοιχεία σας</p>
            </div>

            {error && <div style={{padding:12,borderRadius:10,background:'#FEF2F2',border:'1px solid #FECACA',color:'#991B1B',fontSize:13,marginBottom:16}}>&#10060; {error}</div>}

            {/* Personal Info */}
            <div style={{background:'#F8FAFC',borderRadius:12,padding:16,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,color:'#1E293B',marginBottom:12}}>&#128100; Προσωπικά Στοιχεία</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={lbl}>Όνομα *</label><input type="text" value={form.first_name} onChange={e=>setForm({...form,first_name:e.target.value})} style={inp} placeholder="Ιωάννης"/></div>
                <div><label style={lbl}>Επώνυμο *</label><input type="text" value={form.last_name} onChange={e=>setForm({...form,last_name:e.target.value})} style={inp} placeholder="Παπαδόπουλος"/></div>
              </div>
              <div style={{marginTop:12}}><label style={lbl}>Email *</label><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} style={inp} placeholder="email@example.com"/></div>
              <div style={{marginTop:12}}><label style={lbl}>Κωδικός *</label><input type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} style={inp} placeholder="Τουλάχιστον 8 χαρακτήρες"/></div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:12}}>
                <div><label style={lbl}>Τηλέφωνο</label><input type="tel" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} style={inp} placeholder="2310 xxxxxxx"/></div>
                <div><label style={lbl}>Κινητό *</label><input type="tel" value={form.mobile} onChange={e=>setForm({...form,mobile:e.target.value})} style={inp} placeholder="69x xxx xxxx"/></div>
              </div>
            </div>

            {/* Professional Info */}
            <div style={{background:'#F8FAFC',borderRadius:12,padding:16,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,color:'#1E293B',marginBottom:12}}>&#9878;&#65039; Επαγγελματικά Στοιχεία</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={lbl}>ΑΦΜ *</label><input type="text" value={form.vat_number} onChange={e=>setForm({...form,vat_number:e.target.value})} style={inp} placeholder="9 ψηφία" maxLength={9}/></div>
                <div><label style={lbl}>ΔΟΥ</label><input type="text" value={form.doy} onChange={e=>setForm({...form,doy:e.target.value})} style={inp} placeholder="π.χ. Δ' Θεσσαλονίκης"/></div>
              </div>
              <div style={{marginTop:12}}><label style={lbl}>Επωνυμία Γραφείου / Εταιρείας</label><input type="text" value={form.firm_name} onChange={e=>setForm({...form,firm_name:e.target.value})} style={inp} placeholder="π.χ. Δικηγορικό Γραφείο Παπαδόπουλος & Συνεργάτες"/></div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:12}}>
                <div><label style={lbl}>Αρ. Μητρώου Δ.Σ. *</label><input type="text" value={form.bar_number} onChange={e=>setForm({...form,bar_number:e.target.value})} style={inp} placeholder="Αριθμός μητρώου"/></div>
                <div><label style={lbl}>Δικηγορικός Σύλλογος *</label>
                  <select value={form.bar_association} onChange={e=>setForm({...form,bar_association:e.target.value})} style={{...inp,cursor:'pointer'}}>
                    <option value="">-- Επιλέξτε --</option>
                    {BAR_ASSOCIATIONS.map(b=>(<option key={b} value={b}>{b}</option>))}
                  </select>
                </div>
              </div>
              <div style={{marginTop:12}}><label style={lbl}>Αριθμός υπαλλήλων γραφείου</label>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {EMPLOYEE_OPTIONS.map(opt=>(
                    <button key={opt} type="button" onClick={()=>setForm({...form,employee_count:opt})} style={{
                      padding:'6px 14px',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',
                      border:'1px solid '+(form.employee_count===opt?'#3B82F6':'#E2E8F0'),
                      background:form.employee_count===opt?'#EFF6FF':'#FFF',
                      color:form.employee_count===opt?'#2563EB':'#64748B',
                    }}>{opt}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Office Address */}
            <div style={{background:'#F8FAFC',borderRadius:12,padding:16,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,color:'#1E293B',marginBottom:12}}>&#128205; Διεύθυνση Γραφείου</div>
              <div><label style={lbl}>Διεύθυνση</label><input type="text" value={form.office_address} onChange={e=>setForm({...form,office_address:e.target.value})} style={inp} placeholder="Οδός, Αριθμός"/></div>
              <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginTop:12}}>
                <div><label style={lbl}>Πόλη</label><input type="text" value={form.office_city} onChange={e=>setForm({...form,office_city:e.target.value})} style={inp} placeholder="π.χ. Θεσσαλονίκη"/></div>
                <div><label style={lbl}>Τ.Κ.</label><input type="text" value={form.office_zip} onChange={e=>setForm({...form,office_zip:e.target.value})} style={inp} placeholder="54xxx" maxLength={5}/></div>
              </div>
            </div>

            {/* Legal Areas */}
            <div style={{background:'#F8FAFC',borderRadius:12,padding:16,marginBottom:20}}>
              <div style={{fontSize:13,fontWeight:700,color:'#1E293B',marginBottom:12}}>&#128218; Τομείς Δικαίου (Ειδίκευση)</div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {LEGAL_AREAS.map(area=>(
                  <button key={area} type="button" onClick={()=>toggleLegalArea(area)} style={{
                    padding:'6px 12px',borderRadius:8,fontSize:12,fontWeight:500,cursor:'pointer',
                    border:'1px solid '+(form.legal_areas.includes(area)?'#059669':'#E2E8F0'),
                    background:form.legal_areas.includes(area)?'#F0FDF4':'#FFF',
                    color:form.legal_areas.includes(area)?'#059669':'#64748B',
                  }}>
                    {form.legal_areas.includes(area)?'&#10003; ':''}{area}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <button onClick={handleRegister} disabled={loading} style={{
              width:'100%',padding:'14px',borderRadius:12,
              background:loading?'#94A3B8':'linear-gradient(135deg,#1E293B,#334155)',
              color:'#FFF',fontSize:15,fontWeight:700,border:'none',cursor:loading?'wait':'pointer',
            }}>
              {loading ? '&#9203; Εγγραφή...' : selectedPlan === 'free_trial' ? '&#127873; Ξεκινήστε Δωρεάν για 1 Μήνα' : '&#128179; Συνέχεια στην Πληρωμή'}
            </button>

            <div style={{textAlign:'center',marginTop:12}}>
              <button onClick={()=>setStep(3)} style={{background:'none',border:'none',color:'#3B82F6',fontSize:13,cursor:'pointer',textDecoration:'underline'}}>
                Έχετε ήδη λογαριασμό; Συνδεθείτε
              </button>
            </div>

            <p style={{fontSize:11,color:'#94A3B8',textAlign:'center',marginTop:12}}>
              Με την εγγραφή αποδέχεστε τους Όρους Χρήσης και την Πολιτική Απορρήτου
            </p>
          </div>
        )}

        {/* ============ STEP 3: LOGIN ============ */}
        {step === 3 && (
          <div style={{padding:32}}>
            <div style={{textAlign:'center',marginBottom:24}}>
              <span style={{fontSize:40}}>&#128274;</span>
              <h2 style={{fontSize:20,fontWeight:800,color:'#1E293B',margin:'8px 0 4px'}}>Σύνδεση</h2>
              <p style={{fontSize:13,color:'#64748B'}}>Εισάγετε τα στοιχεία σας</p>
            </div>

            {error && <div style={{padding:12,borderRadius:10,background:'#FEF2F2',border:'1px solid #FECACA',color:'#991B1B',fontSize:13,marginBottom:16}}>&#10060; {error}</div>}

            <div style={{marginBottom:16}}><label style={lbl}>Email</label><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} style={inp}/></div>
            <div style={{marginBottom:20}}><label style={lbl}>Κωδικός</label><input type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} style={inp}/></div>

            <button onClick={handleLogin} disabled={loading} style={{
              width:'100%',padding:'14px',borderRadius:12,background:loading?'#94A3B8':'#1E293B',
              color:'#FFF',fontSize:15,fontWeight:700,border:'none',cursor:loading?'wait':'pointer',
            }}>
              {loading ? '&#9203; Σύνδεση...' : '&#128274; Σύνδεση'}
            </button>

            <div style={{textAlign:'center',marginTop:16}}>
              <button onClick={()=>setStep(4)} style={{background:'none',border:'none',color:'#DC2626',fontSize:13,cursor:'pointer',textDecoration:'underline'}}>
                Ξεχάσατε τον κωδικό σας;
              </button>
            </div>

            <div style={{textAlign:'center',marginTop:8}}>
              <button onClick={()=>setStep(1)} style={{background:'none',border:'none',color:'#3B82F6',fontSize:13,cursor:'pointer',textDecoration:'underline'}}>
                Δεν έχετε λογαριασμό; Εγγραφείτε
              </button>
            </div>
          </div>
        )}

        {/* ============ STEP 4: FORGOT PASSWORD ============ */}
        {step === 4 && (
          <div style={{padding:32}}>
            <button onClick={()=>setStep(3)} style={{padding:'4px 10px',borderRadius:6,border:'1px solid #E2E8F0',background:'#FFF',cursor:'pointer',fontSize:12,marginBottom:16}}>&larr; Πίσω</button>
            <div style={{textAlign:'center',marginBottom:24}}>
              <span style={{fontSize:40}}>&#128272;</span>
              <h2 style={{fontSize:20,fontWeight:800,color:'#1E293B',margin:'8px 0 4px'}}>Επαναφορά Κωδικού</h2>
              <p style={{fontSize:13,color:'#64748B'}}>Εισάγετε το email σας για να λάβετε σύνδεσμο επαναφοράς</p>
            </div>
            {error && <div style={{padding:12,borderRadius:10,background:'#FEF2F2',border:'1px solid #FECACA',color:'#991B1B',fontSize:13,marginBottom:16}}>{error}</div>}
            {form.resetSent ? (
              <div style={{textAlign:'center',padding:'20px 0'}}>
                <div style={{fontSize:48,marginBottom:12}}>&#9993;&#65039;</div>
                <h3 style={{color:'#059669',fontSize:16,fontWeight:700,marginBottom:8}}>Email εστάλη!</h3>
                <p style={{color:'#64748B',fontSize:14}}>Αν υπάρχει λογαριασμός, θα λάβετε σύνδεσμο επαναφοράς.</p>
                <p style={{color:'#94A3B8',fontSize:12,marginTop:12}}>Ελέγξτε και τον φάκελο spam.</p>
              </div>
            ) : (
              <>
                <div style={{marginBottom:20}}><label style={lbl}>Email</label><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} style={inp} placeholder="your@email.com"/></div>
                <button onClick={async()=>{
                  if(!form.email){setError('Εισάγετε email');return;}
                  setLoading(true);setError('');
                  try{
                    const res=await fetch(API+'/api/auth/forgot-password',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email:form.email})});
                    if(res.ok)setForm({...form,resetSent:true});
                    else{const d=await res.json();setError(d.detail||'Σφάλμα');}
                  }catch(e){setError('Σφάλμα σύνδεσης');}
                  setLoading(false);
                }} disabled={loading} style={{width:'100%',padding:'14px',borderRadius:12,background:loading?'#94A3B8':'#DC2626',color:'#FFF',fontSize:15,fontWeight:700,border:'none',cursor:loading?'wait':'pointer'}}>
                  {loading ? 'Αποστολή...' : 'Αποστολή Συνδέσμου'}
                </button>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AuthModal;
